import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { PinAction } from '@components/common';
import TabSections from '@components/Map/TabSections';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { auth, map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	resetRightSideControls,
	setApplyFiltersAvailableState,
	setPinnedFilter,
} from '@slices/map';

import { isEqual } from '@utils/common';
import {
	extractIdsFromAssetTypes,
	getAppliedFilterAttributes,
	getSelectedAssetTypeIds,
	getSelectedAttributes,
	isYesNoAll,
	mapStringToBoolean,
} from '@components/Map/MapControls/Controls/helpers';

import { EControlsType, TEControlsType } from '@enums/common';

import { EAssetFilterNames, filterTabs } from './filterItems';

import styles from './AssetsFilters.module.scss';

const AssetsFilters = () => {
	const {
		appliedAssetsFilterValues,
		colorAssetsBy,
		currentAssetsFilterValues,
		siteAccessAvailable,
		lastUsedTabSection,
		isAcquiredAssetsShownApplied,
	} = useAppSelector(map);

	const {
		accessPermission: {
			networkOperatorAssetEnquirer,
			networkOperatorOrganizationAdministrator,
			networkOperatorAssetViewer,
			assetConsumerOrganizationAdministrator,
			assetConsumerAssetEnquirer,
			assetConsumerAssetViewer,
		},
	} = useAppSelector(auth);

	const [tabs, setTabs] = useState(() => {
		return filterTabs.map((tab, i) => {
			return {
				...tab,
				isActive: lastUsedTabSection ? tab.name === lastUsedTabSection : i === 0,
			};
		});
	});

	const isAssetStateEnabled = (
		networkOperatorOrganizationAdministrator ||
		networkOperatorAssetViewer ||
		networkOperatorAssetEnquirer ||
		assetConsumerOrganizationAdministrator ||
		assetConsumerAssetEnquirer ||
		assetConsumerAssetViewer
	);

	useEffect(() => {
		setTabs((prevTabs) => {
			return prevTabs.map((tab) => {
				if (tab.name === EAssetFilterNames.assetState) {
					return {
						...tab,
						isDisabled: !isAssetStateEnabled,
					};
				}

				return tab;
			});
		});
	}, [isAssetStateEnabled]);

	const dispatch = useAppDispatch();

	const containerRef = useRef<HTMLDivElement>(null);
	const prevColorAssetsBy = useRef(colorAssetsBy);
	const prevFilterValues = useRef(currentAssetsFilterValues);
	const prevSiteAccessAvailable = useRef(siteAccessAvailable);

	const [isPinned, setIsPinned] = useState(false);

	const pinFilter = useCallback((filter: TEControlsType | null) => {
		return dispatch(setPinnedFilter(filter));
	}, [dispatch]);

	useEffect(() => {
		pinFilter(isPinned ? EControlsType.filters : null);
	}, [isPinned, pinFilter]);

	useEffect(() => {
		const isColorAssetByChanged = prevColorAssetsBy.current !== colorAssetsBy;
		const isSiteAccessAvailableChanged =
			mapStringToBoolean(isYesNoAll(appliedAssetsFilterValues.isAvailable)
				? appliedAssetsFilterValues.isAvailable : undefined)
			!== mapStringToBoolean(isYesNoAll(siteAccessAvailable)
				? siteAccessAvailable
				: undefined);
		const currentFilterAssetTypeIds = getSelectedAssetTypeIds(currentAssetsFilterValues.assetType);
		const appliedFilterAssetTypeIds = extractIdsFromAssetTypes(appliedAssetsFilterValues.assetType);
		const currentFilterAttributes = getSelectedAttributes(currentAssetsFilterValues.attributes);
		const appliedFilterAttributes = getAppliedFilterAttributes(appliedAssetsFilterValues);
		const areFilterAssetTypeChanged = !isEqual(appliedFilterAssetTypeIds, currentFilterAssetTypeIds);
		const areFilterAttributesChanged = !isEqual(appliedFilterAttributes, currentFilterAttributes);

		dispatch(setApplyFiltersAvailableState(
			isColorAssetByChanged ||
			isSiteAccessAvailableChanged ||
			areFilterAssetTypeChanged ||
			areFilterAttributesChanged,
		));

		prevColorAssetsBy.current = colorAssetsBy;
		prevFilterValues.current = currentAssetsFilterValues;
		prevSiteAccessAvailable.current = siteAccessAvailable;
	}, [
		appliedAssetsFilterValues,
		colorAssetsBy,
		currentAssetsFilterValues,
		dispatch,
		isAcquiredAssetsShownApplied,
		siteAccessAvailable,
	]);

	useEffect(() => {
		return () => {
			dispatch(setPinnedFilter(null));
		};
	}, [dispatch]);

	useOnClickOutside({
		refs: [containerRef],
		handler: () => dispatch(resetRightSideControls()),
		disableClickOutside: isPinned,
		fireEvent: 'click',
	});

	return (
		<div
			ref={containerRef}
			className={styles.filtersContainer}
		>
			<div className={styles.pinIconContainer}>
				<PinAction
					isPinned={isPinned}
					pinTooltip='Pin'
					setIsPinned={setIsPinned}
					tooltipPlacement='left'
					unpinTooltip='Unpin'
				/>
			</div>
			<TabSections
				setTabState={setTabs}
				tabs={tabs}
			/>
		</div>
	);
};

export default memo(AssetsFilters);

